import { supabase } from "@/lib/supabase"
import { getFunctionsBaseUrl } from "@/utils/getFunctionsBaseUrl"

import type { ShiftQueryRow } from "../../shared/shift-api"

export type ShiftStatus = "Open" | "Filled" | "Cancelled" | "Unfulfilled"

export interface Shift {
  id: string
  peopleId: string | null
  startTime: string // ISO
  endTime: string // ISO
  minutesLate: number | null
  awol: boolean | null
  status: ShiftStatus
  area: string
  type: string
  employer: string
  location: string
  depotId: string | null
  vehicle?: {
    id: string | null
    reg: string | null
  }
  notes: string | null
  preassignedRoute: string | null
  cancelledReason: string | null
  cancelledTime: string | null
  notifications: boolean
  lastEditedBy: string | null
  people?: {
    employer: string | null
    personId: string | null
    person: string | null
    status: string | null
    type: string | null
    primaryPersonId: string | null
    stickyLocation?: string | null
  }
  depot?: {
    id: string | null
    name: string | null
    depotId: string | null
    latitude: number | string | null
    longitude: number | string | null
  }
  timesheet?: {
    id: string | null
    clockedInTime: string | null
    clockedOutTime: string | null
    clockinDepotId: string | null
    clockinCarParkId: string | null
    clockoutDepotId: string | null
    break: number | null
    shiftId: string | null
    personId: string | null
    latitude: number | null
    longitude: number | null
    endLatitude: number | null
    endLongitude: number | null
    status: string | null
    lastEditedBy: string | null
    autoClockOut: boolean | null
    createdAt: string | null
    updatedAt: string | null
    employer: string | null
  }
}

export interface FetchShiftsBody {
  startTime: string // ISO
  endTime: string // ISO
  location?: string
  employer?: string
  type?: string
  areas?: string[]
  page?: number
  pageSize?: number
  include?: Array<"people" | "timesheet" | "depot">
}

export interface FetchShiftsResponse {
  success: boolean
  results: Shift[]
  error?: string
  code?: string
}

export interface ShiftsMetaResponse {
  success: boolean
  areas: string[]
  types: string[]
  locations: string[]
  employers: string[]
  error?: string
  code?: string
}

// Driver ops
export type CancelledReason =
  | "Sickness"
  | "FamilyEmergency"
  | "OtherWorkCommitments"
  | "HivedCapacity"
  | "RetailerRequest"
  | "Offboarded"
  | "RequestedByDriver"
  | "RequestedByDsp"
  | "UnavailabilityRequest"
  | "MovedPosition"
  | "Onboarded"
  | "SheriffTypeChanged"

export interface ListShiftsResponse {
  success: boolean
  results: Shift[]
  limit?: number
  offset?: number
  total?: number
  error?: string
  code?: string
}

export interface BookShiftBody {
  id: string // shift id
}

export interface BookShiftResponse {
  success: boolean
  shift: Shift
  error?: string
  code?: string
}

export interface CancelShiftBody {
  id: string
  cancelledReason: CancelledReason
  auditComment?: string | null
}

export interface CancelShiftResponse {
  success: boolean
  shift: Shift
  republishedShift: Shift | null
  error?: string
  code?: string
}

export interface SubstituteShiftBody {
  shiftId: string
  personId: string // substitute person id
}

export interface SubstituteShiftResponse {
  success: boolean
  shift: Shift
  error?: string
  code?: string
}

export interface CreateShiftBody {
  startTime: string
  endTime: string
  area: string
  type: string
  employer: string
  location: string
  depotId?: string | null
  notes?: string | null
  peopleId?: string | null
  notifications?: boolean
  preassignedRoute?: string | null
  vehicleReg?: string | null
}

export interface UpdateShiftBody {
  id: string
  startTime?: string
  endTime?: string
  area?: string
  type?: string
  employer?: string
  location?: string
  depotId?: string | null
  notes?: string | null
  peopleId?: string | null
  notifications?: boolean
  preassignedRoute?: string | null
  vehicleReg?: string | null
  minutesLate?: number | null
  awol?: boolean | null
  cancelledReason?: CancelledReason | null
  auditComment?: string | null
}

async function withAuthHeaders() {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  return {
    Authorization: token ? `Bearer ${token}` : "",
    "Content-Type": "application/json",
  } as Record<string, string>
}

function buildQueryString(params: Record<string, unknown>): string {
  const search = new URLSearchParams()

  const appendParam = (key: string, value: unknown) => {
    if (value === undefined) return
    if (value === null) {
      search.append(key, "")
      return
    }
    if (Array.isArray(value)) {
      value.forEach((item) => appendParam(`${key}[]`, item))
      return
    }
    if (typeof value === "object") {
      Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
        appendParam(`${key}[${k}]`, v)
      })
      return
    }
    search.append(key, String(value))
  }

  Object.entries(params).forEach(([key, value]) => {
    appendParam(key, value)
  })

  return search.toString()
}

export const shiftsService = {
  async fetchShifts(body: FetchShiftsBody): Promise<FetchShiftsResponse> {
    const headers = await withAuthHeaders()
    const first = body.pageSize ?? 10000
    const offset = ((body.page ?? 1) - 1) * first

    // Build where according to edge function schema
    const where: Record<string, unknown> = {
      startTime: { after: body.startTime, before: body.endTime },
    }
    if (body.location) where.location = { equals: body.location }
    if (body.employer) where.employer = { equals: body.employer }
    if (body.type) where.type = { equals: body.type }
    if (body.areas && body.areas.length > 0) {
      where.area = body.areas.length === 1 ? { equals: body.areas[0] } : { in: body.areas }
    }

    const include = body.include ?? ["people", "timesheet"]
    const query = buildQueryString({
      first,
      offset,
      where,
      include,
    })
    const res = await fetch(`${getFunctionsBaseUrl()}/shifts/query?${query}`, {
      method: "GET",
      headers,
    })

    const json = await res.json()
    if (!res.ok || json?.success === false) {
      throw new Error(
        (json && (json.error || json.message)) || `Failed to fetch shifts (status ${res.status})`,
      )
    }

    const rows: ShiftQueryRow[] = Array.isArray(json?.results)
      ? (json.results as ShiftQueryRow[])
      : []

    const results: Shift[] = rows.map((r) => ({
      id: r.id,
      peopleId: r.peopleId ?? null,
      startTime: r.startTime,
      endTime: r.endTime,
      minutesLate: r.minutesLate ?? null,
      awol: r.awol ?? null,
      status: r.status,
      area: r.area,
      type: r.type,
      employer: r.employer,
      location: r.location,
      depotId: r.depotId ?? null,
      vehicle: { id: r.vehicle?.id ?? null, reg: r.vehicle?.reg ?? null },
      notes: r.notes ?? null,
      preassignedRoute: r.preassignedRoute ?? null,
      cancelledReason: r.cancelledReason ?? null,
      cancelledTime: r.cancelledTime ?? null,
      notifications: Boolean(r.notifications ?? false),
      lastEditedBy: r.lastEditedBy ?? null,
      people: {
        employer: r.people?.sheriffEmployer ?? null,
        personId: r.people?.personId ?? null,
        person: r.people?.person ?? null,
        status: r.people?.status ?? null,
        type: r.people?.sheriffType ?? null,
        primaryPersonId: r.people?.primaryPersonId ?? null,
        stickyLocation: r.people?.stickyLocation ?? null,
      },
      depot: r.depot
        ? {
            id: r.depot.id ?? null,
            name: r.depot.name ?? null,
            depotId: r.depot.depotId ?? null,
            latitude: r.depot.latitude ?? null,
            longitude: r.depot.longitude ?? null,
          }
        : undefined,
      timesheet: r.timesheet
        ? {
            id: r.timesheet.id ?? null,
            clockedInTime: r.timesheet.clockedInTime ?? null,
            clockedOutTime: r.timesheet.clockedOutTime ?? null,
            clockinDepotId: r.timesheet.clockinDepotId ?? null,
            clockinCarParkId: r.timesheet.clockinCarParkId ?? null,
            clockoutDepotId: r.timesheet.clockoutDepotId ?? null,
            break: r.timesheet.break ?? null,
            shiftId: r.timesheet.shiftId ?? null,
            personId: r.timesheet.personId ?? null,
            latitude: r.timesheet.latitude ?? null,
            longitude: r.timesheet.longitude ?? null,
            endLatitude: r.timesheet.endLatitude ?? null,
            endLongitude: r.timesheet.endLongitude ?? null,
            status: r.timesheet.status ?? null,
            lastEditedBy: r.timesheet.lastEditedBy ?? null,
            autoClockOut: r.timesheet.autoClockOut ?? null,
            createdAt: r.timesheet.createdAt ?? null,
            updatedAt: r.timesheet.updatedAt ?? null,
            employer: r.timesheet.employer ?? null,
          }
        : undefined,
    }))

    return { success: true, results }
  },

  async createShift(body: CreateShiftBody): Promise<Shift> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/shifts`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    })
    const json = (await res.json()) as {
      success: boolean
      shift?: Shift
      error?: string
    }
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to create shift (status ${res.status})`)
    }
    return json.shift!
  },

  async updateShift(body: UpdateShiftBody): Promise<Shift> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/shifts`, {
      method: "PUT",
      headers,
      body: JSON.stringify(body),
    })
    const json = (await res.json()) as {
      success: boolean
      shift?: Shift
      error?: string
    }
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to update shift (status ${res.status})`)
    }
    return json.shift!
  },

  async fetchMeta(): Promise<ShiftsMetaResponse> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/shifts/meta`, {
      method: "GET",
      headers,
    })
    const json = (await res.json()) as ShiftsMetaResponse
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to fetch shifts meta (status ${res.status})`)
    }
    return json
  },

  // Driver endpoints
  async fetchAvailableShifts(): Promise<ListShiftsResponse> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/shifts/available`, {
      method: "GET",
      headers,
    })
    const json = (await res.json()) as ListShiftsResponse
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to fetch available shifts (status ${res.status})`)
    }
    return json
  },

  async fetchUpcomingShifts(): Promise<ListShiftsResponse> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/shifts/upcoming`, {
      method: "GET",
      headers,
    })
    const json = (await res.json()) as ListShiftsResponse
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to fetch upcoming shifts (status ${res.status})`)
    }
    return json
  },

  async fetchPastShifts(): Promise<ListShiftsResponse> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/shifts/past`, {
      method: "GET",
      headers,
    })
    const json = (await res.json()) as ListShiftsResponse
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to fetch past shifts (status ${res.status})`)
    }
    return json
  },

  async bookShift(body: BookShiftBody): Promise<Shift> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/shifts/book`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    })
    const json = (await res.json()) as BookShiftResponse
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to book shift (status ${res.status})`)
    }
    return json.shift
  },

  async cancelShift(body: CancelShiftBody): Promise<CancelShiftResponse> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/shifts/cancel`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    })
    const json = (await res.json()) as CancelShiftResponse
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to cancel shift (status ${res.status})`)
    }
    return json
  },

  async substituteShift(body: SubstituteShiftBody): Promise<Shift> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/shifts/substitute`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    })
    const json = (await res.json()) as SubstituteShiftResponse
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to substitute shift (status ${res.status})`)
    }
    return json.shift
  },

  async bulkCreateShifts(shifts: CreateShiftBody[]): Promise<{ shifts: Shift[]; count: number }> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/shifts/bulk-create`, {
      method: "POST",
      headers,
      body: JSON.stringify({ shifts }),
    })
    const json = (await res.json()) as {
      success: boolean
      shifts: Shift[]
      count: number
      error?: string
      code?: string
    }
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to bulk create shifts (status ${res.status})`)
    }
    return { shifts: json.shifts, count: json.count }
  },

  async csvUploadShifts(
    csvData: string,
  ): Promise<{ shifts: Shift[]; count: number; errors?: string[] }> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/shifts/csv-upload`, {
      method: "POST",
      headers,
      body: JSON.stringify({ csvData }),
    })
    const json = (await res.json()) as {
      success: boolean
      shifts: Shift[]
      count: number
      errors?: string[]
      error?: string
      code?: string
    }
    if (!res.ok || json.success === false) {
      const csvRowErrors =
        json.errors && json.errors.length > 0 ? `\n\n${json.errors.join("\n\n")}` : ""
      throw new Error(
        `${json.error || `Failed to upload CSV (status ${res.status})`}${csvRowErrors}`,
      )
    }
    return {
      shifts: json.shifts,
      count: json.count,
      errors: json.errors,
    }
  },

  async fetchPersonShifts(personId: string): Promise<{
    upcoming: Shift[]
    past: Shift[]
  }> {
    const now = new Date().toISOString()
    // First, get the person's UUID by their Airtable person_id
    const { data: personData, error: personError } = await supabase
      .from("people")
      .select("id")
      .eq("person_id", personId)
      .maybeSingle()

    if (personError) {
      throw new Error(`Failed to fetch person: ${personError.message}`)
    }

    if (!personData) {
      // No person found, return empty arrays
      return { upcoming: [], past: [] }
    }

    const personUuid = personData.id

    // Fetch upcoming shifts with timesheets
    const { data: upcomingData, error: upcomingError } = await supabase
      .from("shifts")
      .select(
        `
        *,
        person:people(*),
        vehicles!shifts_vehicle_id_fkey(id, registration_display),
        timesheets!timesheets_shift_id_fkey(*)
      `,
      )
      .eq("person_id", personUuid)
      .gte("start_time", now)
      .order("start_time", { ascending: true })
      .limit(1000)

    if (upcomingError) {
      throw new Error(`Failed to fetch upcoming shifts: ${upcomingError.message}`)
    }

    // Fetch past shifts with timesheets
    const { data: pastData, error: pastError } = await supabase
      .from("shifts")
      .select(
        `
        *,
        person:people(*),
        vehicles!shifts_vehicle_id_fkey(id, registration_display),
        timesheets!timesheets_shift_id_fkey(*)
      `,
      )
      .eq("person_id", personUuid)
      .lt("start_time", now)
      .order("start_time", { ascending: false })
      .limit(1000)

    if (pastError) {
      throw new Error(`Failed to fetch past shifts: ${pastError.message}`)
    }

    // Map the results
    type ShiftTableRow = {
      id: string
      person_id: string | null
      start_time: string
      end_time: string
      minutes_late: number | null
      awol: boolean | null
      status: ShiftStatus
      area: string
      type: string
      employer: string
      location: string
      depot_id: string | null
      vehicle_id: string | null
      vehicle_reg: string | null
      notes: string | null
      preassigned_route: string | null
      cancelled_reason: string | null
      cancelled_time: string | null
      notifications: boolean | null
      last_edited_by: string | null
      person?: {
        employer?: string | null
        person?: string | null
        status?: string | null
        type?: string | null
        primary_person_id?: string | null
      } | null
      vehicles?: { id: string; registration_display: string } | null
      timesheets?: Array<{
        id?: string | null
        status?: string | null
        auto_clock_out?: boolean | null
        clocked_in_time?: string | null
        clocked_out_time?: string | null
        clockin_depot_id?: string | null
        clockin_car_park_id?: string | null
        clockout_depot_id?: string | null
      }> | null
    }

    const mapRow = (r: ShiftTableRow): Shift => {
      // Get the first/most recent timesheet if multiple exist
      const timesheet = r.timesheets && r.timesheets.length > 0 ? r.timesheets[0] : null

      return {
        id: r.id,
        peopleId: r.person_id ?? null,
        startTime: r.start_time,
        endTime: r.end_time,
        minutesLate: r.minutes_late ?? null,
        awol: r.awol ?? null,
        status: r.status,
        area: r.area,
        type: r.type,
        employer: r.employer,
        location: r.location,
        depotId: r.depot_id ?? null,
        // Keyless vehicles come through vehicle_id; older shifts only have the free-text plate.
        vehicle: {
          id: r.vehicles?.id ?? null,
          reg: r.vehicles?.registration_display ?? r.vehicle_reg ?? null,
        },
        notes: r.notes ?? null,
        preassignedRoute: r.preassigned_route ?? null,
        cancelledReason: r.cancelled_reason ?? null,
        cancelledTime: r.cancelled_time ?? null,
        notifications: Boolean(r.notifications ?? false),
        lastEditedBy: r.last_edited_by ?? null,
        people: r.person
          ? {
              employer: r.person.employer ?? null,
              personId: r.person_id ?? null,
              person: r.person.person ?? null,
              status: r.person.status ?? null,
              type: r.person.type ?? null,
              primaryPersonId: r.person.primary_person_id ?? null,
            }
          : undefined,
        timesheet: timesheet
          ? {
              id: timesheet.id ?? null,
              clockedInTime: timesheet.clocked_in_time ?? null,
              clockedOutTime: timesheet.clocked_out_time ?? null,
              clockinDepotId: timesheet.clockin_depot_id ?? null,
              clockinCarParkId: timesheet.clockin_car_park_id ?? null,
              clockoutDepotId: timesheet.clockout_depot_id ?? null,
              break: null,
              shiftId: null,
              personId: null,
              latitude: null,
              longitude: null,
              endLatitude: null,
              endLongitude: null,
              status: timesheet.status ?? null,
              lastEditedBy: null,
              autoClockOut: timesheet.auto_clock_out ?? null,
              createdAt: null,
              updatedAt: null,
              employer: null,
            }
          : undefined,
      }
    }

    return {
      upcoming: (upcomingData ?? []).map(mapRow),
      past: (pastData ?? []).map(mapRow),
    }
  },
}
